import type { CSSProperties } from 'react';
import type { Plan, PlanFilters, Profile, AvailabilityStatus, PlanColorKey } from './types';
import { BUSY_PLAN_COLOR_KEY, PLAN_COLOR_HEX } from './types';
import { sortPlansChronologically } from './dates';

export function isPlanVisibleToViewer(plan: Plan, viewerId: string): boolean {
  if (plan.deletedAt) return false;
  if (plan.ownerId === viewerId) return true;
  return plan.visibility === 'shared';
}

/** Space partners may edit each other's visible plans; never write redacted Busy stubs. */
export function canMutatePlan(plan: Plan, viewerId: string): boolean {
  if (!viewerId || plan.deletedAt) return false;
  if (plan.ownerId === viewerId) return true;
  if (!isPlanVisibleToViewer(plan, viewerId)) return false;
  // RPC redacts hidden partner details to title "Busy" — saving would destroy the real title.
  if (plan.title === 'Busy') return false;
  return true;
}

export function getDisplayTitle(
  plan: Plan,
  viewerId: string,
  busyLabel: string,
): string {
  if (
    plan.ownerId !== viewerId &&
    plan.hideDetailsFromPartner &&
    plan.availabilityStatus !== 'none'
  ) {
    return busyLabel;
  }
  return plan.title;
}

export function getDisplayDescription(
  plan: Plan,
  viewerId: string,
): string {
  if (
    plan.ownerId !== viewerId &&
    plan.hideDetailsFromPartner
  ) {
    return '';
  }
  return plan.description;
}

export function filterPlans(
  plans: Plan[],
  filters: PlanFilters,
  viewerId: string,
): Plan[] {
  return plans.filter((plan) => {
    if (!isPlanVisibleToViewer(plan, viewerId)) return false;
    if (filters.owners !== 'all' && !filters.owners.includes(plan.ownerId)) {
      return false;
    }
    if (filters.visibility !== 'all' && plan.visibility !== filters.visibility) {
      return false;
    }
    if (filters.status !== 'all' && plan.status !== filters.status) {
      return false;
    }
    if (
      filters.availability !== 'all' &&
      plan.availabilityStatus !== filters.availability
    ) {
      return false;
    }
    if (filters.query.trim()) {
      const q = filters.query.trim().toLowerCase();
      const title = getDisplayTitle(plan, viewerId, 'Busy').toLowerCase();
      const description = getDisplayDescription(plan, viewerId).toLowerCase();
      const hay = `${title} ${description} ${plan.planDate ?? ''}`;
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function groupPlansByDate(plans: Plan[]): Map<string, Plan[]> {
  const map = new Map<string, Plan[]>();
  for (const plan of sortPlansChronologically(plans)) {
    const key = plan.planDate ?? '__no_date__';
    const list = map.get(key) ?? [];
    list.push(plan);
    map.set(key, list);
  }
  return map;
}

export type SharedGroupKey = 'today' | 'this_week' | 'later' | 'no_date' | 'completed';

export function groupSharedPlans(
  plans: Plan[],
  todayKey: string,
  isInThisWeek: (dateKey: string) => boolean,
): Record<SharedGroupKey, Plan[]> {
  const result: Record<SharedGroupKey, Plan[]> = {
    today: [],
    this_week: [],
    later: [],
    no_date: [],
    completed: [],
  };

  for (const plan of sortPlansChronologically(plans)) {
    if (plan.visibility !== 'shared') continue;
    if (plan.status === 'completed') {
      result.completed.push(plan);
      continue;
    }
    if (!plan.planDate) {
      result.no_date.push(plan);
      continue;
    }
    if (plan.planDate === todayKey) {
      result.today.push(plan);
    } else if (isInThisWeek(plan.planDate) && plan.planDate > todayKey) {
      result.this_week.push(plan);
    } else {
      // future outside this week, or past incomplete — keep visible under later
      result.later.push(plan);
    }
  }
  return result;
}

export interface FreeInterval {
  start: string;
  end: string;
}

/** Very simple free-slot estimate for a day based on timed busy plans */
export function computeFreeIntervals(
  plans: Plan[],
  dayStart = '08:00',
  dayEnd = '22:00',
): FreeInterval[] {
  const busy = plans
    .filter(
      (p) =>
        p.status === 'active' &&
        !p.isAllDay &&
        p.startTime &&
        p.endTime &&
        (p.availabilityStatus === 'busy' || p.availabilityStatus === 'tentative'),
    )
    .map((p) => ({ start: p.startTime!, end: p.endTime! }))
    .sort((a, b) => a.start.localeCompare(b.start));

  const free: FreeInterval[] = [];
  let cursor = dayStart;
  for (const block of busy) {
    if (block.start > cursor) {
      free.push({ start: cursor, end: block.start });
    }
    if (block.end > cursor) cursor = block.end;
  }
  if (cursor < dayEnd) {
    free.push({ start: cursor, end: dayEnd });
  }
  return free;
}

export function availabilityLabelKey(status: AvailabilityStatus): string {
  switch (status) {
    case 'busy':
      return 'availability.busy';
    case 'free':
      return 'availability.free';
    case 'tentative':
      return 'availability.tentative';
    default:
      return 'availability.none';
  }
}

export function resolvePlanColor(
  plan: Plan,
  profiles: Profile[],
  viewerId: string,
): string {
  // RPC redacts Busy plans to title "Busy" and nulls color_key for partners.
  if (plan.ownerId !== viewerId && plan.title === 'Busy') {
    return PLAN_COLOR_HEX[BUSY_PLAN_COLOR_KEY];
  }
  if (plan.colorKey && PLAN_COLOR_HEX[plan.colorKey]) {
    return PLAN_COLOR_HEX[plan.colorKey];
  }
  if (plan.categoryColor) return plan.categoryColor;
  const owner = profiles.find((p) => p.id === plan.ownerId);
  return owner?.accentColor ?? PLAN_COLOR_HEX.indigo;
}

/**
 * Personal strip/fill for PlanChip — always from plan owner accent (owner_id),
 * never from the viewing user. Busy/redacted → graphite.
 */
export function resolveOwnerAccentColor(
  plan: Plan,
  profiles: Profile[],
  viewerId: string,
): string {
  if (plan.ownerId !== viewerId && plan.title === 'Busy') {
    return PLAN_COLOR_HEX[BUSY_PLAN_COLOR_KEY];
  }
  if (
    plan.ownerId !== viewerId &&
    plan.hideDetailsFromPartner &&
    plan.availabilityStatus !== 'none'
  ) {
    return PLAN_COLOR_HEX[BUSY_PLAN_COLOR_KEY];
  }
  const owner = profiles.find((p) => p.id === plan.ownerId);
  return owner?.accentColor ?? PLAN_COLOR_HEX.indigo;
}

/**
 * Muted fill for the whole calendar day cell from the first plan by time.
 * Busy-for-partner → graphite; no rainbow gradients when multiple plans.
 */
export function resolveDayCellFillColor(
  dayPlans: Plan[],
  profiles: Profile[],
  viewerId: string,
): string | null {
  if (dayPlans.length === 0) return null;
  const first = sortPlansChronologically(dayPlans)[0];
  if (!first) return null;
  return resolvePlanColor(first, profiles, viewerId);
}

export function dayCellSurfaceStyle(hex: string): CSSProperties {
  return {
    backgroundColor: `color-mix(in srgb, ${hex} 20%, transparent)`,
  } as CSSProperties;
}

export function planChipSurfaceStyle(hex: string): CSSProperties {
  return {
    backgroundColor: `color-mix(in srgb, ${hex} 22%, transparent)`,
    borderColor: `color-mix(in srgb, ${hex} 45%, transparent)`,
    ['--plan-chip-rail' as keyof CSSProperties]: hex,
    ['--plan-chip-hover' as keyof CSSProperties]: `color-mix(in srgb, ${hex} 32%, transparent)`,
  } as CSSProperties;
}

export function isPlanColorKey(value: string | null | undefined): value is PlanColorKey {
  return Boolean(value && value in PLAN_COLOR_HEX);
}
